import { useMutation, useQueryClient } from '@tanstack/react-query';
import TodoService, {
  ICreateTodoParams,
} from '@clickersphere/core/services/TodoService';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { TodoQueryData, TODOS_QUERY_KEY } from './useTodos';

export function useCreateTodo() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const { isPending: createTodoIsPending, mutateAsync: createTodoAsync } =
    useMutation({
      mutationFn: async (data: ICreateTodoParams) => {
        const todo = await TodoService.create(data);
        return todo as TodoQueryData;
      },
      onMutate: async (variables) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(TODOS_QUERY_KEY),
          exact: false,
        });

        const tempId = String(crypto.randomUUID());

        queryClient.setQueriesData<TodoQueryData[]>(
          { queryKey: buildQueryKey(TODOS_QUERY_KEY), exact: false },
          (old) => [
            ...(old ?? []).concat({
              ...variables,
              id: tempId,
              tenantId: '',
              done: false,
              userId: '',
              createdAt: Date.now(),
              updatedAt: Date.now(),
              queryStatus: 'pending',
            }),
          ],
        );

        return { tempId };
      },

      onSuccess: async (data, _variables, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(TODOS_QUERY_KEY),
          exact: false,
        });

        queryClient.setQueriesData<TodoQueryData[]>(
          { queryKey: buildQueryKey(TODOS_QUERY_KEY), exact: false },
          (old) =>
            old?.map((todo) => (todo.id === context.tempId ? data : todo)),
        );
      },

      onError: async (_error, _variables, context) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey(TODOS_QUERY_KEY),
          exact: false,
        });

        queryClient.setQueriesData<TodoQueryData[]>(
          { queryKey: buildQueryKey(TODOS_QUERY_KEY), exact: false },
          (old) => old?.filter((todo) => todo.id !== context?.tempId),
        );
      },
    });

  return {
    createTodoAsync,
    createTodoIsPending,
  };
}
